import React, {ChangeEvent, FC, useState} from 'react';

import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Menu from '@mui/material/Menu';
import Avatar from '@mui/material/Avatar';
import DialogPanel from '@/components/DialogPanel';

import AnnouncementIcon from '@mui/icons-material/Announcement';
import ListIcon from '@mui/icons-material/List';
import EventIcon from '@mui/icons-material/Event';
import SaveIcon from '@mui/icons-material/Save';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';

import {SelectInput} from '@geomatico/geocomponents';

import Panel from '@/components/Panels/common/Panel';
import SubTitle from '@/components/forms/SubTitle';
import InfoBox from '@/components/Panels/common/InfoBox';

import {useTranslation} from 'react-i18next';

import {ParcelSummary} from '@/domain/entities/ParcelSummary';
import {Dispute, DisputeStatus} from '@/domain/entities/Dispute';

import {enumToOptions, toFormattedDateDDMMYY} from '@/utils';

import {DISPUTE_STATUS_CHIP_CONFIGURATION, PANEL_WIDTH} from '@/config';
import {SelectedParcelMode} from '@/components/Panels/Parcel/index';
import {User} from '@/domain/entities/User';
import Chip from '@mui/material/Chip';

//TYPES
export type DisputePanelProps = {
  user?: User,
  selectedParcelUuid: string,
  parcelSummary?: ParcelSummary,
  dispute?: Dispute,
  onDisputeSaved: (dispute: Omit<Dispute, 'geom'>) => void,
  onSelectedParcelModeChanged: (selectedParcelMode: SelectedParcelMode) => void
};

export type HelpMenuProps = {
  anchorEl: null | HTMLElement,
  onClose: () => void
}

const HelperGeometryMenu: FC<HelpMenuProps> = ({anchorEl, onClose}) => {

  const {t} = useTranslation();

  return <Menu
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={onClose}
    sx={{m: 1}}
  >
    <Stack direction="column" gap={1} p={2}>
      <SubTitle label={t('disputePanel.helperGeometryMenu.title')} icon={<EditIcon sx={styles.infoIcon}/>}/>
      <Stack direction="row" gap={1}>
        <Avatar sx={{ width: 18, height: 18, fontSize: '12px', fontWeight: 700 }}>1</Avatar>
        <Typography variant="caption">{t('disputePanel.helperGeometryMenu.stepOne')}</Typography>
      </Stack>
      <Stack direction="row" gap={1}>
        <Avatar sx={{ width: 18, height: 18, fontSize: '12px', fontWeight: 700 }}>2</Avatar>
        <Typography variant="caption">{t('disputePanel.helperGeometryMenu.stepTwo')}</Typography>
      </Stack>
      <Stack direction="row" gap={1}>
        <Avatar sx={{ width: 18, height: 18, fontSize: '12px', fontWeight: 700 }}>3</Avatar>
        <Typography variant="caption">{t('disputePanel.helperGeometryMenu.stepThree')}</Typography>
      </Stack>
      <img alt={t('disputePanel.modifyGeometry')} src="/assets/helpers/geometryHelper.png"/>
    </Stack>
  </Menu>;
};

const DisputePanel: FC<DisputePanelProps> = ({user, selectedParcelUuid, parcelSummary, dispute, onDisputeSaved, onSelectedParcelModeChanged}) => {

  const {t} = useTranslation();
  const DISPUTE_STATUS_OPTIONS = enumToOptions(DisputeStatus, t, 'disputePanel');

  if(!parcelSummary) return;
  if(!selectedParcelUuid) return;

  const {polygon, parcel, subparcel} = parcelSummary;
  
  const initialDispute = {
    parcelUuid: selectedParcelUuid,
    status: DisputeStatus.PENDING,
    geom: parcelSummary.geom,
  };
  
  const [editingDispute, setEditingDispute] = useState<Dispute>(dispute || initialDispute);
  const [isExitAlertPanelOpen, setExitPanelOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditingDispute({
      ...editingDispute,
      description: e.target.value
    });
  };

  const handleDisputeSaved = () => {
    if(editingDispute) {
      onDisputeSaved({
        uuid: editingDispute.uuid,
        parcelUuid: editingDispute.parcelUuid,
        status: editingDispute.status,
        description: editingDispute.description
      });
      setEditingDispute(initialDispute);
      onSelectedParcelModeChanged(SelectedParcelMode.INFO);
    }
  };

  const handleStatusChange = (statusId: string | undefined) => setEditingDispute({
    ...editingDispute,
    status: statusId as DisputeStatus
  });

  const handleExit = () => {
    setEditingDispute(initialDispute);
    onSelectedParcelModeChanged(SelectedParcelMode.INFO);
  };

  const handleHelperMenuOpen = (e: React.MouseEvent<SVGSVGElement>) => {
    setAnchorEl(e.currentTarget as unknown as HTMLElement);
  };

  const handleHelperMenuClose = () => {
    setAnchorEl(null);
  };

  return <>
    <Panel width={PANEL_WIDTH.dispute}>
      <Stack direction="column">
        <Stack direction="row" sx={{alignItems: 'center', gap: 1, mb: 0}}>
          <AnnouncementIcon sx={{fontSize: '28px', color: 'primary.main'}}/>
          <Typography variant="h6" sx={{color: 'primary.main', m: 0, pb: 0.5}}>{t('disputePanel.title')}</Typography>
        </Stack>
        <Stack direction="row">
          <Typography variant="body1" sx={{color: 'grey.700', m: 0, pb: 2}}>
            {t('disputePanel.descriptionText')}
            <InfoOutlinedIcon onClick={handleHelperMenuOpen} sx={{fontSize: '22px', pt: 0.5}}/>
          </Typography>

        </Stack>
        <Stack direction="row" sx={{justifyContent: 'space-between', gap: 1}}>
          <InfoBox label={t('parcelPanel.polygon')} value={polygon}/>
          <InfoBox label={t('parcelPanel.parcel')} value={parcel}/>
          <InfoBox label={t('parcelPanel.subparcel')} value={subparcel}/>
        </Stack>

        <Divider sx={{my: 2}}/>

        <Stack sx={{gap: 2}}>
          <SubTitle label={t('disputePanel.createdAt')} icon={<EventIcon sx={styles.infoIcon}/>}>
            <Typography variant="body1" sx={{pl: 3.5}}>{toFormattedDateDDMMYY(dispute?.createdAt)}</Typography>
          </SubTitle>

          <SubTitle label={t('disputePanel.updatedAt')} icon={<EventIcon sx={styles.infoIcon}/>}>
            <Typography variant="body1" sx={{pl: 3.5}}>{toFormattedDateDDMMYY(dispute?.updatedAt)}</Typography>
          </SubTitle>
        </Stack>

        <Divider sx={{my: 2}}/>
        <Stack sx={{gap: 2}}>
          <SubTitle label={`${t('disputePanel.status')}*`} icon={<ListIcon sx={styles.infoIcon}/>}>
            {
              user?.role === 'USER'
                ? <Chip label={t('disputePanel.pending')} sx={chipDispute}/>
                : <SelectInput
                  options={DISPUTE_STATUS_OPTIONS}
                  onOptionChange={handleStatusChange}
                  selectedOptionId={editingDispute?.status || DisputeStatus.IN_REVIEW}
                  placeholderLabel={t('disputePanel.status')}
                  dense={true}
                  sx={{mt:1}}
                />
            }
            
          </SubTitle>
        
          <SubTitle label={t('disputePanel.description')} icon={<ListIcon sx={styles.infoIcon}/>}>
            <TextField
              value={editingDispute?.description || ''}
              multiline={true}
              maxRows={4}
              onChange={handleDescriptionChange}
              sx={{mt: 1}}
            />
          </SubTitle>
        </Stack>
        <Stack direction="row" sx={{gap: 2}}>
          <Button variant="outlined" startIcon={<CancelPresentationIcon/>} sx={styles.smallButton} onClick={() => setExitPanelOpen(true)}>
            {t('disputePanel.exit')}
          </Button>
          <Button variant="contained" startIcon={<SaveIcon/>} sx={styles.smallButton} onClick={handleDisputeSaved} disabled={!editingDispute?.status}>
            {t('disputePanel.save')}
          </Button>
        </Stack>
      
      </Stack>
    </Panel>
    {
      isExitAlertPanelOpen && <DialogPanel isOpen={true} label={t('disputePanel.acceptExit.title')} onClose={() => {}}>
        <Stack sx={{m: 1}}>
          <Typography gutterBottom>{t('disputePanel.acceptExit.description')}</Typography>
          <Typography sx={{mb: 2}}>{t('disputePanel.acceptExit.confirmation')}</Typography>
          <Button variant="outlined" sx={styles.bigButton} onClick={handleExit}>
            {t('disputePanel.acceptExit.title')}
          </Button>
        </Stack>
      </DialogPanel>
    }
    <HelperGeometryMenu anchorEl={anchorEl} onClose={handleHelperMenuClose}/>
  </>;
};
export default DisputePanel;

const statusConfig = DISPUTE_STATUS_CHIP_CONFIGURATION['PENDING'];

const chipDispute = {
  maxWidth: '150px',
  border: '1px solid white',
  color: statusConfig[1],
  bgcolor: statusConfig[0],
  fontWeight: 700,
  '& .MuiChip-icon': {
    color: 'white'
  }
};

const styles = {
  localizationBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    bgcolor: 'grey.200',
    width: '33%',
    height: '70px',
    borderRadius: 1
  },
  infoIcon: {
    fontSize: '20px',
    color: 'primary.main'
  },
  subtitle: {
    color: 'text.secondary',
    m: 0,
    pb: 0.25,
    lineHeight: '16px'
  },
  smallButton: {
    width: '150px',
    mt: 2,
    alignSelf: 'flex-end'
  },
  bigButton: {
    mt: 2
  }
};